"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import toast from "react-hot-toast";
import { useZStore } from "@/utils/z-store";
import { WORKSPACE_HEADER } from "@/shared/workspace-constants";
import { WorkspaceSummary } from "@/app/zustand-store/slices/workspaceSlice";

/**
 * Reads the caller's workspaces + active workspace and exposes a switch action.
 * Backed by the (non-flag-gated) /api/workspaces read+switch API.
 */
export function useWorkspaces() {
  const { data, actions } = useZStore();
  const router = useRouter();

  const fetchWorkspaces = useCallback(async () => {
    try {
      const res = await axios.get("/api/workspaces");
      const list: WorkspaceSummary[] = res.data?.workspaces || [];
      actions.setWorkspaces(list);

      // Keep a valid persisted active workspace, else adopt the server's.
      const persisted = useZStore.getState().data.activeWorkspaceId;
      const stillValid = persisted && list.some((w) => w.id === persisted);
      actions.setActiveWorkspaceId(
        stillValid ? persisted : res.data?.activeWorkspaceId || null,
      );
    } catch (error) {
      console.error("Failed to load workspaces:", error);
    }
  }, [actions]);

  const switchWorkspace = useCallback(
    async (id: string, redirectTo: string | null = "/dashboard/home") => {
      if (id === useZStore.getState().data.activeWorkspaceId) return;
      try {
        await axios.post(`/api/workspaces/${id}/switch`);
        // Point subsequent requests at the new workspace BEFORE any refetch.
        axios.defaults.headers.common[WORKSPACE_HEADER] = id;
        // Drop the previous workspace's cached data, then flip the active id.
        // The WorkspaceBoundary (keyed on activeWorkspaceId) remounts the
        // dashboard subtree so all data refetches cleanly — no full reload.
        actions.resetWorkspaceScopedData();
        actions.setActiveWorkspaceId(id);
        // Navigate to a safe landing page (default: home, which avoids stale
        // resource-specific views). Pass `null` to stay on the current page and
        // let the boundary remount refetch it in place (e.g. the /users
        // switcher, which should keep the user on /users).
        if (redirectTo) router.replace(redirectTo);
      } catch (error) {
        console.error("Failed to switch workspace:", error);
        toast.error("Failed to switch workspace.", {
          position: "bottom-right",
        });
      }
    },
    [actions, router],
  );

  const createWorkspace = useCallback(
    async (name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed) return false;
      try {
        const res = await axios.post("/api/workspaces", { name: trimmed });
        const created = res.data?.workspace;
        if (created?.id) {
          // Switch into the new workspace (persists + full reload).
          await switchWorkspace(created.id);
        } else {
          await fetchWorkspaces();
        }
        return true;
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to create workspace.";
        toast.error(message, { position: "bottom-right" });
        return false;
      }
    },
    [switchWorkspace, fetchWorkspaces],
  );

  const workspaces = data.workspaces || [];
  const activeWorkspace =
    workspaces.find((w) => w.id === data.activeWorkspaceId) || null;

  return {
    workspaces,
    activeWorkspaceId: data.activeWorkspaceId ?? null,
    activeWorkspace,
    workspacesLoaded: !!data.workspacesLoaded,
    fetchWorkspaces,
    switchWorkspace,
    createWorkspace,
  };
}
